import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Switch,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
	deletePromoClientSector,
	getPromoClientSectorById,
	postPromoClientSector,
	putPromoClientSector,
} from "../../../../api/promo-client-sector";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptySector() {
	return {
		name: "",
		sortOrder: 0,
		active: true,
	};
}

export default function EditPromoClientSectorPage() {
	const sectorId = useParams().id;
	const history = useHistory();
	const [sector, setSector] = useState(getEmptySector());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		let mounted = true;
		if (!sectorId) {
			disableLoadingData();
			return () => {
				mounted = false;
			};
		}
		getPromoClientSectorById(sectorId)
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) {
					const s = res.data || {};
					setSector({
						name: s.name || "",
						sortOrder: s.sortOrder ?? 0,
						active: s.active !== false,
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudo obtener el sector." });
				history.push("/promo-client-sectors");
			});
		return () => {
			mounted = false;
		};
	}, [sectorId, disableLoadingData, history]);

	function saveSector() {
		const body = {
			name: String(sector.name || "").trim(),
			active: !!sector.active,
		};

		if (!body.name) {
			alertError({ customMessage: "El nombre es obligatorio." });
			return;
		}

		if (!sectorId) {
			postPromoClientSector(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({ title: "Guardado", customMessage: "Sector creado correctamente." });
						history.push("/promo-client-sectors");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudo crear el sector." });
				});
		} else {
			putPromoClientSector(sectorId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({ title: "Guardado", customMessage: "Cambios guardados correctamente." });
						history.push("/promo-client-sectors");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudieron guardar los cambios." });
				});
		}
	}

	const handleChange = (field) => (event) => {
		setSector({ ...sector, [field]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;

	return (
		<>
			<Card>
				<CardHeader title={sectorId ? "Editar sector de clientes" : "Nuevo sector de clientes"} />
				<CardBody>
					<TextField
						id="sector-name"
						label="Nombre del sector"
						helperText="Ej: Farma y healthcare, Otros sectores. El orden se cambia desde el listado de sectores."
						value={sector.name}
						onChange={handleChange("name")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						required
					/>
				</CardBody>
			</Card>

			<Card>
				<CardBody>
					<FormControlLabel
						control={
							<Switch
								checked={!!sector.active}
								onChange={(e) => setSector({ ...sector, active: e.target.checked })}
								color="primary"
							/>
						}
						label={sector.active ? "Activo (visible en evenely.com)" : "Inactivo (oculto en la web)"}
					/>
					<div style={{ fontSize: 12, color: "#888" }}>
						Los sectores inactivos y sus clientes no se muestran en la web pública.
					</div>
				</CardBody>
			</Card>

			<Button
				onClick={() => history.push("/promo-client-sectors")}
				variant="outlined"
				style={{ marginRight: 20 }}
			>
				Volver
			</Button>
			<Button onClick={saveSector} variant="outlined" color="primary" style={{ marginRight: 20 }}>
				Guardar sector
			</Button>
			{sectorId && (
				<>
					<MuiThemeProvider theme={theme}>
						<Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
							Eliminar sector
						</Button>
					</MuiThemeProvider>

					<ConfirmDialog
						title="¿Seguro que quieres eliminar este sector?"
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePromoClientSector(sectorId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Sector eliminado correctamente." });
										history.push("/promo-client-sectors");
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar el sector." });
								});
						}}
					>
						No se puede eliminar si tiene clientes asignados.
					</ConfirmDialog>
				</>
			)}
		</>
	);
}
